import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import localeData from 'dayjs/plugin/localeData';
import { isNullOrUndefined } from '../../util/is-null-or-undefined.util';

import {
  IDataSelecaoItem,
  IMesSelecaoConfig,
  MesSelecao,
} from './month-date-picker.model';

@Component({
  selector: 'app-month-date-picker',
  standalone: false,
  templateUrl: './month-date-picker.component.html',
  styleUrls: ['./month-date-picker.component.scss'],
})
export class MonthDatePickerComponent implements OnInit, OnChanges {
  innerValue: Date;
  model = '';
  mesSelecao: MesSelecao = null;
  placeHolder = 'selecione';

  @Input() config: IMesSelecaoConfig = {};
  @Output() modelChange = new EventEmitter();
  @Input() shouldValidate: boolean = true;

  constructor(private _elementRef: ElementRef) {
    dayjs.extend(localeData);
    dayjs.locale('pt-br');
  }

  ngOnInit() {
    const mesLista = dayjs.monthsShort();
    const currentDate = new Date();
    this.config.maxAno = !isNullOrUndefined(this.config.maxAno)
      ? this.config.maxAno
      : currentDate.getFullYear();
    this.config.maxMes = !isNullOrUndefined(this.config.maxMes)
      ? this.config.maxMes
      : currentDate.getMonth() + 1;
    this.config.minAno = !isNullOrUndefined(this.config.minAno)
      ? this.config.minAno
      : 0;
    this.config.minMes = !isNullOrUndefined(this.config.minMes)
      ? this.config.minMes
      : 0;
    this.config.locale = !isNullOrUndefined(this.config.locale)
      ? this.config.locale
      : 'pt-br';
    this.mesSelecao = new MesSelecao(
      mesLista,
      this.config.maxAno,
      this.config.maxMes,
      this.config.minAno,
      this.config.minMes
    );
    if (this.config.placeHolder !== undefined) {
      this.placeHolder = this.config.placeHolder;
    }

    this.validate();
  }

  /**
   * Valida o valor da data inicial
   */
  validate(): void {
    if (this.shouldValidate) {
      // O valor inicial deve ser menor ou igual a data máxima
      const dataMaxima = new Date(this.config.maxAno, this.config.maxMes - 1);
      const dataMinima = new Date(this.config.minAno, this.config.minMes - 1);

      if (this.config.valorInicial !== undefined) {
        // Se a data inicial estiver fora do intervalo
        if (
          this.config.valorInicial > dataMaxima ||
          (this.config.minAno !== 0 && this.config.valorInicial < dataMinima)
        ) {
          this.config.valorInicial = dataMinima;
          // Se a data data inicial estiver dentro do intervalo esta receberá o limite inferior
        } else if (
          this.config.valorInicial < dataMaxima &&
          this.config.minAno !== 0 &&
          this.config.valorInicial > dataMinima
        ) {
          this.config.valorInicial = dataMinima;
        }
        const text = this.config.displayMesExtenso
          ? dayjs(this.config.valorInicial).format('MMMM/YYYY')
          : dayjs(this.config.valorInicial).format('MMM/YYYY');
        this.model = this.formatarPascalCase(text);
      } else {
        this.config.valorInicial = dataMaxima;
      }
    } else {
      const text = this.config.displayMesExtenso
        ? dayjs(this.config.valorInicial).format('MMMM/YYYY')
        : dayjs(this.config.valorInicial).format('MMM/YYYY');
      this.model = this.formatarPascalCase(text);
    }
  }

  /**
   * Realiza a atualização do valor inicial
   * @param mudanca
   */
  ngOnChanges(mudanca: SimpleChanges) {
    if (mudanca.config.currentValue) {
      this.config.valorInicial = mudanca.config.currentValue.valorInicial;
      this.validate();
    }
  }

  calendarioIconeClick() {
    this.mesSelecao.definirDataCorrente(new Date());
    this.mesSelecao.definirEstado();
  }

  selecionarItemClick(item: IDataSelecaoItem) {
    this.selecaoMes(item);
  }

  selecaoMes(item: IDataSelecaoItem) {
    this.mesSelecao.displayMes = item.text;
    this.mesSelecao.displayMesIndex = item.value;

    this.mesSelecao.selecaoMes = item.text;
    this.mesSelecao.selecaoMesIndex = item.value;
    this.mesSelecao.selecaoAno = this.mesSelecao.displayAno;
    const indexMes = this.mesSelecao.selecaoMesIndex;
    let textMes = '';
    if (!this.config.displayMesExtenso) {
      textMes = this.formatarPascalCase(
        this.mesSelecao.selectionItems[indexMes - 1].text
      );
    } else {
      // Get text of name of the month in brazilian portuguese after getting month by the index
      textMes = this.formatarPascalCase(
        dayjs()
          .month(indexMes - 1)
          .format('MMMM')
      );
    }

    this.model = textMes + '/' + this.mesSelecao.selecaoAno;
    this.mesSelecao.estado = 'fechado';
    this.innerValue = new Date(this.mesSelecao.selecaoAno, indexMes - 1, 1);
    this.innerValue = new Date(dayjs(this.innerValue).endOf('month').format());
    this.modelChange.next(this.innerValue);
  }

  voltarAno() {
    const currentDate = new Date();

    if (
      this.config.minAno === undefined ||
      this.mesSelecao.displayAno > this.config.minAno
    ) {
      this.mesSelecao.displayAno--;
    }

    this.mesSelecao.selectionItems.forEach((item) => {
      item.classItem = 'mes-item-bloqueado';
      item.bloqueado = true;

      // Quando o ano máximo é o mesmo que o mínimo
      if (this.config.maxAno === this.config.minAno) {
        // O ano corrente no datepicker for igual ao ano limite
        if (this.config.maxAno === this.mesSelecao.displayAno) {
          if (
            this.config.minMes <= this.config.maxMes &&
            item.value >= this.config.minMes &&
            item.value <= this.config.maxMes
          ) {
            item.classItem = 'mes-item-desbloqueado';
            item.bloqueado = false;
          }
        }
        // O ano corrente for maior que o ano minimo e menor que o ano máximo todos os meses devem estar desbloqueados
      } else if (
        this.mesSelecao.displayAno > this.config.minAno &&
        this.mesSelecao.displayAno < this.config.maxAno
      ) {
        item.classItem = 'mes-item-desbloqueado';
        item.bloqueado = false;
        // O ano corrente no datepicker for igual ao ano máximo
      } else if (this.mesSelecao.displayAno === this.config.minAno) {
        if (item.value >= this.config.minMes) {
          item.classItem = 'mes-item-desbloqueado';
          item.bloqueado = false;
        }
      }
    });
  }

  proximoAno() {
    if (
      this.config.maxAno === undefined ||
      this.mesSelecao.displayAno < this.config.maxAno
    ) {
      this.mesSelecao.displayAno++;
    }

    this.mesSelecao.selectionItems.forEach((item) => {
      item.classItem = 'mes-item-bloqueado';
      item.bloqueado = true;

      // Quando o ano máximo é o mesmo que o mínimo
      if (this.config.maxAno === this.config.minAno) {
        // O ano corrente no datepicker for diferente do ano limite
        if (this.config.maxAno === this.mesSelecao.displayAno) {
          if (
            this.config.minMes <= this.config.maxMes &&
            item.value >= this.config.minMes &&
            item.value <= this.config.maxMes
          ) {
            item.classItem = 'mes-item-desbloqueado';
            item.bloqueado = false;
          }
        }
        // O ano corrente no datepicker for maior que o ano minimo e menor que o ano máximo todos os meses devem estar desbloqueados
      } else if (
        this.mesSelecao.displayAno > this.config.minAno &&
        this.mesSelecao.displayAno < this.config.maxAno
      ) {
        item.classItem = 'mes-item-desbloqueado';
        item.bloqueado = false;
        // O ano corrente no datepicker for igual ao ano mínimo
      } else if (this.mesSelecao.displayAno === this.config.minAno) {
        if (item.value >= this.config.minMes) {
          item.classItem = 'mes-item-desbloqueado';
          item.bloqueado = false;
        }
        // O ano corrente no datepicker for igual ao ano máximo
      } else if (this.mesSelecao.displayAno === this.config.maxAno) {
        if (item.value <= this.config.maxMes) {
          item.classItem = 'mes-item-desbloqueado';
          item.bloqueado = false;
        }
      }
    });
  }

  formatarPascalCase(text: string) {
    if (text !== null && text !== undefined) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    } else {
      return null;
    }
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public onClick(event: MouseEvent, targetElement: HTMLElement): void {
    if (!targetElement) {
      return;
    }

    const clickedInside =
      this._elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.mesSelecao.estado = 'fechado';
    }
  }
}
